import { computeLineTotal, formatCents } from './money';
import type { BillAdjust, TimeAdjust } from './schemas/money';

/**
 * How one billed part prints on an invoice (Inventory v2 12b, decision 7), worked out once for
 * the server that writes it and the bill sheet that previews it, so the two can't disagree.
 *
 * The part line is at the list price; what comes off it prints under it. The base is the job's
 * price for the part (`startCents`), or a new price typed in the sheet; below the list it prints
 * as "Price adjusted to $X each". A % or $ off comes off the base (an amount never more than the
 * line). No charge (R11) keeps the line at $0 with nothing under it.
 */
export interface BilledPart {
  noCharge: boolean;
  /** The unit price on the part line: the list price, or a new price above it. */
  unitCents: number;
  /** The discount lines under it, each a positive amount that comes off. */
  offs: { name: string; cents: number }[];
  /** What the customer is charged for it, before tax. */
  netCents: number;
}

export function billedPart(p: {
  qty: number;
  listCents: number;
  startCents: number;
  /** `total` (a set amount) is for hours only (ticket 13). */
  adjust?: BillAdjust | TimeAdjust;
  /** False for a charge (12c), billed whole: "Price adjusted to $40.00", without "each". */
  each?: boolean;
  /** Hours (ticket 13): a new rate prints "Price adjusted to $95.00/h". */
  perHour?: boolean;
}): BilledPart {
  if (p.adjust?.kind === 'free')
    return { noCharge: true, unitCents: 0, offs: [], netCents: 0 };
  if (p.adjust?.kind === 'total') return setAmount(p.qty, p.listCents, p.adjust.value);
  const base = p.adjust?.kind === 'price' ? p.adjust.value : p.startCents;
  const unitCents = Math.max(base, p.listCents);
  const printed = computeLineTotal(p.qty, unitCents);
  const gross = computeLineTotal(p.qty, base);
  const offs: { name: string; cents: number }[] = [];
  if (printed > gross) {
    offs.push({
      name: `Price adjusted to ${formatCents(base)}${p.each === false ? '' : p.perHour ? '/h' : ' each'}`,
      cents: printed - gross,
    });
  }
  if (p.adjust?.kind === 'pct') {
    const off = Math.round((gross * p.adjust.value) / 100);
    if (off > 0)
      offs.push({ name: `Discount ${Number(p.adjust.value.toFixed(2))}%`, cents: off });
  } else if (p.adjust?.kind === 'amt') {
    const off = Math.min(gross, p.adjust.value);
    if (off > 0) offs.push({ name: 'Discount', cents: off });
  }
  return {
    noCharge: false,
    unitCents,
    offs,
    netCents: printed - offs.reduce((n, o) => n + o.cents, 0),
  };
}

/**
 * Hours billed at a set amount (ticket 13, the owner 2026-10-09: "if I tell you a repair will
 * cost $200, I bill $200"): the customer is charged exactly `target`, whatever the hours at the
 * rate come to. Below the rate math, the hours print at the rate and the rest comes off as "Set
 * amount $X". Above it, the rate rises to the lowest one the hours print `target` or more at,
 * and anything over (a cent, when the hours can't print it exactly) comes off the same way.
 */
function setAmount(qty: number, rateCents: number, target: number): BilledPart {
  let unitCents = rateCents;
  if (computeLineTotal(qty, rateCents) < target) {
    unitCents = Math.ceil(target / qty);
    while (unitCents > 0 && computeLineTotal(qty, unitCents - 1) >= target)
      unitCents -= 1;
    while (computeLineTotal(qty, unitCents) < target) unitCents += 1;
  }
  const printed = computeLineTotal(qty, unitCents);
  return {
    noCharge: false,
    unitCents,
    offs:
      printed > target
        ? [{ name: `Set amount ${formatCents(target)}`, cents: printed - target }]
        : [],
    netCents: target,
  };
}

/**
 * `cents` × `num` / `den`, rounded half away from zero: what came off a part line follows it when
 * part of it comes back (12b), on a draft and in what is owed back. Nothing billed scales to 0.
 */
export function scaleCents(cents: number, num: number, den: number): number {
  if (den === 0) return 0;
  const v = Math.round((Math.abs(cents) * num) / den);
  // Never -0: a line that bills nothing is 0.
  return cents < 0 && v !== 0 ? -v : v;
}
